import type { StatusTagColor } from '../../components/ui/StatusTag/StatusTag';

/**
 * Global-search result-page constants.
 *
 * Vue parity: `RW-ERP-Service/src/types/common/global-search-constants.ts`.
 * The status → tag-colour maps are copied VERBATIM (minus the `-tag`
 * suffix, which the shared `StatusTag` primitive appends itself).
 */

/** Wire keys — Vue `DomainKeyName`. */
export const SEARCH_DOMAIN = {
  ORDERS: 'orders',
  STAGED_ORDERS: 'dealerOrders',
} as const;

export type SearchDomain = (typeof SEARCH_DOMAIN)[keyof typeof SEARCH_DOMAIN];

/** Vue `DomainDisplayName` for the two domains this app serves. */
export const SEARCH_DOMAIN_LABEL: Record<SearchDomain, string> = {
  [SEARCH_DOMAIN.ORDERS]: 'Sales orders',
  [SEARCH_DOMAIN.STAGED_ORDERS]: 'Staged orders',
};

/** One sort instruction the results endpoint accepts. */
export type SearchSort = { attribute: string; order: 'Asc' | 'Desc' };

/** Default sort per domain — Vue `ResultScreen.vue` tab props. */
export const SEARCH_DOMAIN_SORT: Record<SearchDomain, SearchSort> = {
  [SEARCH_DOMAIN.ORDERS]: { attribute: 'processedAt', order: 'Desc' },
  [SEARCH_DOMAIN.STAGED_ORDERS]: { attribute: 'orderCreatedDate', order: 'Desc' },
};

/** Vue `ERROR_MESSAGE` — the point-in-time-expired retry trigger. */
export const PIT_EXPIRED_MESSAGE = 'Point in time id has expired';

export const SEARCH_RESULTS_PAGE_SIZE = 25;

/** Vue `GLOBAL_STATUS` — fulfillment/order-state tag colours. */
export const STATUS_TAG_COLOR: Record<string, StatusTagColor> = {
  fulfilled: 'green',
  unfulfilled: 'black',
  'partially fulfilled': 'orange',
  scheduled: 'dark-blue',
  'on hold': 'yellow',
  cancelled: 'light-black',
  mv_pfas: 'violet',
  mv_price: 'dark-green',
  mv_address: 'pink',
  mv_product: 'light-green',
  mv_total: 'sky-blue',
  mv_required: 'red',
  manual_verification_required: 'red',
  draft: 'black',
  accepted: 'green',
  canceled: 'red',
  completed: 'blue',
  expired: 'light-black',
};

/** Vue `GLOBAL_SUB_STATUS` — payment-state tag colours. */
export const PAYMENT_TAG_COLOR: Record<string, StatusTagColor> = {
  pending: 'yellow',
  paid: 'dark-green',
  'partially paid': 'dark-blue',
  'partially refunded': 'orange',
  refunded: 'pink',
  overdue: 'red',
  voided: 'red',
  authorized: 'light-green',
  accepted: 'green',
  cancelled: 'red',
  completed: 'blue',
  expired: 'light-black',
};
